/**
 * Attendance events (piece 34, *Time capture* and *The employee file*;
 * decision 12851; T3857): Late, No Show and Call-Off recorded as facts at time
 * approval, listed in the Employee page's Attendance section, and offered as
 * the cited fact when a write-up starts. 🔨 An event is appended and never
 * edited or removed; a correction is a new event and the first stays. 🔒 No
 * points: the reason is the recorder's own words.
 */

import { fill, t, type StringKey } from '../strings';
import { hasDayShape } from '../zonedTime';

/** 🔒 The three kinds, as the `AttendanceEventKind` enum stores them. */
export const ATTENDANCE_EVENT_KINDS = ['Late', 'NoShow', 'CallOff'] as const;
export type AttendanceEventKind = (typeof ATTENDANCE_EVENT_KINDS)[number];

/** Each kind's name as a person reads it. */
export const ATTENDANCE_KIND_LABEL_KEYS: Readonly<Record<AttendanceEventKind, StringKey>> = {
  Late: 'hr.attendance.kind.late',
  NoShow: 'hr.attendance.kind.noShow',
  CallOff: 'hr.attendance.kind.callOff',
};

/** A kind in words; an unknown value reads as it came. */
export function attendanceKindLabel(kind: string): string {
  const key = ATTENDANCE_KIND_LABEL_KEYS[kind as AttendanceEventKind];
  return key ? t(key) : kind;
}

/** Record Attendance's write on Time Awaiting Approval, relative to the API root. */
export const ATTENDANCE_RECORD_PATH = '/time/approvals/attendance';

/** The longest reason kept, a sentence or two. */
export const ATTENDANCE_REASON_MAXIMUM = 500;
/** 🔨 A day holds no more minutes than this, so a typed slip of the keyboard is refused rather than kept. */
export const ATTENDANCE_MINUTES_MAXIMUM = 1440;

/** What Record Attendance sends: the worker's day being approved, the kind, the minutes where Late, and the reason. */
export interface AttendanceEventDraft {
  employmentId: string;
  /** The day on the queue, `YYYY-MM-DD`; the event happened on it. */
  workDate: string;
  kind: AttendanceEventKind | '';
  /** Whole minutes late, typed; asked for Late alone. */
  minutesLate: string;
  reason: string;
}

/**
 * 🔨 Why an event is refused, as sentences, or an empty list: a kind of the
 * three, a day written `YYYY-MM-DD`, whole minutes above nought for Late and
 * none for the other two, and a reason within its length.
 */
export function attendanceEventProblems(draft: AttendanceEventDraft): string[] {
  const problems: string[] = [];
  if (!ATTENDANCE_EVENT_KINDS.includes(draft.kind as AttendanceEventKind)) problems.push('An attendance event is Late, No Show or Call-Off.');
  if (typeof draft.workDate !== 'string' || !hasDayShape(draft.workDate)) problems.push('An attendance event names its day as YYYY-MM-DD.');
  const minutes = String(draft.minutesLate ?? '').trim();
  if (draft.kind === 'Late') {
    if (!/^\d+$/.test(minutes) || Number(minutes) < 1 || Number(minutes) > ATTENDANCE_MINUTES_MAXIMUM) problems.push(`A Late event names the whole minutes late, from 1 to ${ATTENDANCE_MINUTES_MAXIMUM}.`);
  } else if (minutes !== '') {
    problems.push('Only a Late event names minutes late.');
  }
  const reason = String(draft.reason ?? '').trim();
  if (reason === '') problems.push('An attendance event gives its reason.');
  else if (reason.length > ATTENDANCE_REASON_MAXIMUM) problems.push(`The reason is ${ATTENDANCE_REASON_MAXIMUM} characters or fewer.`);
  return problems;
}

/** One recorded event, as the Attendance section and a write-up's pick read it. */
export interface AttendanceEventRow {
  id: string;
  kind: AttendanceEventKind;
  /** The day it happened, `YYYY-MM-DD`. */
  occurredOn: string;
  minutesLate: number | null;
  reason: string;
  recordedByName: string;
  /** When it was recorded, as an instant. */
  recordedAt: string;
}

/** 🔒 The event's name as the section reads it: *Late · 14 minutes*, or the kind alone. */
export function attendanceEventLabel(row: Pick<AttendanceEventRow, 'kind' | 'minutesLate'>): string {
  const kind = attendanceKindLabel(row.kind);
  return row.kind === 'Late' && row.minutesLate !== null ? `${kind} · ${fill(t('hr.attendance.minutes'), { minutes: String(row.minutesLate) })}` : kind;
}

/** The event's caption: its reason and who recorded it, *Van would not start · recorded at approval by Dana Reyes*. */
export function attendanceEventDetail(row: Pick<AttendanceEventRow, 'reason' | 'recordedByName'>): string {
  return `${row.reason} · ${fill(t('hr.attendance.recordedBy'), { name: row.recordedByName })}`;
}

/**
 * 🔒 Decision 12851: the choices a write-up's form offers as its cited fact,
 * one per event of the person's job, newest first, each naming its day, its
 * kind and minutes and its reason. The write-up keeps the one picked in
 * `citedAttendanceEventId?`.
 */
export function attendanceCitationChoices(rows: readonly AttendanceEventRow[]): { value: string; label: string }[] {
  return [...rows]
    .sort((a, b) => b.occurredOn.localeCompare(a.occurredOn) || b.recordedAt.localeCompare(a.recordedAt))
    .map((row) => ({ value: row.id, label: `${row.occurredOn} · ${attendanceEventLabel(row)} · ${row.reason}` }));
}
